import { apiClient, toServiceError } from '@/services/http/client'
import type {
  DocumentCategory,
  DocumentWorkspaceService,
  EvidenceAccessGrant,
  EvidenceDocumentVersion,
  EvidenceUploadResult,
} from '@/types/document.types'

async function sha256(file: File): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer())
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('')
}

function extension(name: string): string {
  const value = name.includes('.') ? name.split('.').pop() ?? '' : ''
  return value.toLowerCase()
}

async function safe<T>(operation: () => Promise<T>): Promise<T> {
  try { return await operation() } catch (error) { throw toServiceError(error) }
}

export const documentEvidenceHttp = {
  upload: async (input: {
    file: File
    category: DocumentCategory
    customerId: string
    assessmentYear?: string
    financialYear?: string
    workspaceService?: DocumentWorkspaceService
    description?: string
  }): Promise<EvidenceUploadResult> => safe(async () => {
    const expectedHash = await sha256(input.file)
    const intent = (await apiClient.post<EvidenceUploadResult>('/documents/upload-intents', {
      name: input.file.name,
      category: input.category,
      linked_entity_type: 'individual_customer',
      linked_entity_id: input.customerId,
      file_extension: extension(input.file.name),
      declared_mime: input.file.type || 'application/octet-stream',
      expected_size: input.file.size,
      expected_sha256: expectedHash,
      assessment_year: input.assessmentYear,
      financial_year: input.financialYear,
      workspace_service: input.workspaceService,
      description: input.description,
      access_level: 'client',
    })).data
    const body = new FormData()
    body.append('file', input.file)
    const version = (await apiClient.post<EvidenceDocumentVersion>(
      intent.completion_path.replace(/^\/api\/v1/, ''), body,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )).data
    return { ...intent, version }
  }),

  replace: async (input: {
    documentId: string
    replacesVersionId: string
    file: File
  }): Promise<EvidenceDocumentVersion> => safe(async () => {
    const expectedHash = await sha256(input.file)
    const version = (await apiClient.post<EvidenceDocumentVersion>(
      `/documents/${input.documentId}/versions`, {
        name: input.file.name,
        file_extension: extension(input.file.name),
        declared_mime: input.file.type || 'application/octet-stream',
        expected_size: input.file.size,
        expected_sha256: expectedHash,
        replaces_version_id: input.replacesVersionId,
      },
      { headers: { 'Idempotency-Key': crypto.randomUUID() } },
    )).data
    const body = new FormData()
    body.append('file', input.file)
    return (await apiClient.post<EvidenceDocumentVersion>(
      `/documents/${input.documentId}/versions/${version.id}:complete`, body,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )).data
  }),

  versions: (documentId: string): Promise<EvidenceDocumentVersion[]> => safe(async () => (
    await apiClient.get<EvidenceDocumentVersion[]>(`/documents/${documentId}/evidence-versions`)
  ).data),

  overrideMismatch: (
    documentId: string, versionId: string, reason: string,
  ): Promise<EvidenceDocumentVersion> => safe(async () => (
    await apiClient.post<EvidenceDocumentVersion>(
      `/documents/${documentId}/versions/${versionId}:override-mismatch`, { reason },
    )
  ).data),

  previewGrant: (
    caseId: string, documentId: string, versionId: string, page: number,
  ): Promise<EvidenceAccessGrant> => safe(async () => (
    await apiClient.get<EvidenceAccessGrant>(`/documents/${documentId}/preview`, {
      params: { version_id: versionId, case_id: caseId, page },
    })
  ).data),

  downloadGrant: (
    caseId: string, documentId: string, versionId: string,
  ): Promise<EvidenceAccessGrant> => safe(async () => (
    await apiClient.get<EvidenceAccessGrant>(
      `/documents/${documentId}/versions/${versionId}/download`,
      { params: { case_id: caseId } },
    )
  ).data),

  redeem: (accessUrl: string): Promise<Blob> => safe(async () => (
    await apiClient.get<Blob>(accessUrl, { responseType: 'blob' })
  ).data),
}
